import { useState, useEffect } from 'react';
import './style.scss';
import { ClientSideStorageService } from '../../services/client-side-storage.service';
import CookieConsentSettingsModal from './components/CookieConsentSettingsModal';
import FocusTrap from '../Shared/FocusTrap';
import { IServerSideRendered } from '../Shared/Base/server-side-rendered';
import { useTexts } from '../../hooks/texts.hook';
import { SanitizerService } from '../../services/sanitizer.service';
import { callApiEndpoint } from './helpers/cookie-consent.helper';

export interface ICookieConsentModalTexts {
  cookieconsenttext: string;
  functionalcookieshelptext: string;
}

export interface ICookieConsentModalSettings {
  cookiepageurl: string;
}

type ICookieConsentModalProps = IServerSideRendered<ICookieConsentModalTexts, ICookieConsentModalSettings>;

function CookieConsentModal({ settings, texts }: ICookieConsentModalProps) {
  // === HOOKS ===
  const { t } = useTexts();

  // === STATE ===
  const cookiePageUrl = settings.cookiepageurl;
  const [show, setShow] = useState(() => !ClientSideStorageService.isConsentStatusSet() && window.location.pathname !== cookiePageUrl);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    function handleHideCookieConsent() {
      setShow(false);
    }

    const handleShowCookieConsent = () => {
      setShow(true);
      setShowModal(false);
    };
    const handleShowCookieConsentSettingsModal = () => {
      setShow(false);
      setShowModal(true);
    };

    document.addEventListener('hide-cookie-consent-modal', handleHideCookieConsent);
    document.addEventListener('show-cookie-consent-modal', handleShowCookieConsent);
    document.addEventListener('show-cookie-consent-settings-modal', handleShowCookieConsentSettingsModal);

    return () => {
      document.removeEventListener('hide-cookie-consent-modal', handleHideCookieConsent);
      document.removeEventListener('show-cookie-consent-modal', handleShowCookieConsent);
      document.removeEventListener('show-cookie-consent-settings-modal', handleShowCookieConsentSettingsModal);
    };
  }, []);

  async function handleApproveAllClick() {
    const success = ClientSideStorageService.approveAll();
    setShow(!success);

    if (success) {
      document.dispatchEvent(new CustomEvent('consent-status-approved'));
    }
    await callApiEndpoint('all', success);
  }

  async function handleApproveNecessaryClick() {
    const success = ClientSideStorageService.approveNecessary();
    setShow(!success);

    if (success) {
      document.dispatchEvent(new CustomEvent('consent-status-approved'));
    }
    await callApiEndpoint('necessary', success);
  }

  return (
    <>
      <CookieConsentSettingsModal show={showModal} onClose={() => setShowModal(false)} texts={texts} />
      {show && (
        <FocusTrap isVisible={show}>
          <div className="modal d-block fade show" aria-labelledby="cookie-consent-modal-title" aria-modal="true" role="dialog">
            <div className="modal-dialog modal-dialog-centered cookie-consent-modal__modal-dialog">
              <div className="modal-content">
                <div className="modal-header pb-0">
                  <h1 id="cookie-consent-modal-title" className="modal-title h3">
                    {t('Den här webbplatsen använder kakor', 'This website uses cookies')}
                  </h1>
                </div>
                <div className="modal-body">
                  <div className="last-child-margin-bottom-none" dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(texts.cookieconsenttext) }}></div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary flex-fill my-1 my-lg-0 cookie-consent-modal__btn"
                    onClick={() => {
                      setShow(false);
                      setShowModal(true);
                    }}
                  >
                    {t('Anpassa kakor', 'Manage cookies')}
                  </button>
                  <button type="button" className="btn btn-primary flex-fill mx-lg-2 my-2 my-lg-0 cookie-consent-modal__btn" onClick={handleApproveNecessaryClick}>
                    {t('Godkänn nödvändiga', 'Approve necessary')}
                  </button>
                  <button type="button" className="btn btn-primary flex-fill my-1 my-lg-0 cookie-consent-modal__btn" onClick={handleApproveAllClick}>
                    {t('Godkänn alla', 'Approve all')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </FocusTrap>
      )}
      {show && <div className="modal-backdrop fade show"></div>}
    </>
  );
}

export default CookieConsentModal;
